import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useRelayEnvironment } from 'react-relay';

import { MIN_VALUE_ITEMS_FOR_BLOCKING_SCREEN_LOADING, TIMEOUT_BEFORE_AUTO_SAVING } from '../const';
import { findPhaseScheduleItem } from '../functions';
import { transformDataForServer } from '../gantTransformData';
import { ScheduleItemTypeInput } from '../graphql/__generated__/CreateScheduleItemMutation.graphql';
import UpdateBulkScheduleItemMutation from '../graphql/mutations/UpdateBulkScheduleItemMutation';
import { ComponentsDispatch, RootState } from '../store';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setterGant } from '../store/slices/gantSlice';
import { addSpinnerItem } from '../store/slices/spinnerSlice';
import { ItemType, MappingItemIdIndexType, SavingScheduleItemsProcess } from '../types';
import useCommitScheduleItem from './useCommitScheduleItem';
import useEffectBuffer from './useEffectBuffer';

const useSaveToServer = () => {
    const environment = useRelayEnvironment();
    const dispatch = useDispatch<ComponentsDispatch>();
    const [commitCreate, isInFlightCreate] = useMutation(UpdateBulkScheduleItemMutation);
    const { getCommitBulkScheduleItem } = useCommitScheduleItem();
    const connectionItemsId = useSelector<RootState, string>((state) => state.gant.connectionItemsId);

    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const savingProcess = useSelector<RootState, SavingScheduleItemsProcess>((state) => state.gant.savingProcess);
    const inputItems = useSelector<RootState, ItemType[]>((state) => state.gantItems.inputItems);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);

    useEffect(() => {
        if (isInFlightCreate) {
            dispatch(setterGant({ savingProcess: 'processing' }));
        } else {
            dispatch(
                setterGant({
                    savingProcess: savingProcess === 'processing' ? 'finish' : 'none',
                }),
            );
        }
    }, [isInFlightCreate]);

    const saveDataToServer = (newItems: ItemType[]) => {
        const listItemForSaving: ItemType[] = [];

        for (const item of inputItems) {
            const itemForCheck = { ...newItems[mappingItemIdIndex[item.id]] };

            const oldItem = { ...item };
            delete oldItem.idIndex;
            delete oldItem.hideForCollapse;

            const newItem = { ...itemForCheck };
            delete newItem.idIndex;
            delete newItem.hideForCollapse;

            if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
                if (!newItem.projectId && oldItem.projectId) {
                    throw new Error(
                        `Broken field projectId. Previous value: ${oldItem.projectId}. New value: ${newItem.projectId}`,
                    );
                }

                if (!newItem.phaseId && oldItem.phaseId) {
                    throw new Error(
                        `Broken field phaseId. Previous value: ${oldItem.phaseId}. New value: ${newItem.phaseId}`,
                    );
                }

                const itemForSaving = { ...newItems[mappingItemIdIndex[item.id]] };

                if (!newItem.phaseId && !oldItem.phaseId) {
                    itemForSaving.phaseId = findPhaseScheduleItem(oldItem.id, environment) || undefined;
                }

                listItemForSaving.push(itemForSaving);
            }
        }

        if (listItemForSaving.length > MIN_VALUE_ITEMS_FOR_BLOCKING_SCREEN_LOADING) {
            dispatch(addSpinnerItem('savingProcess'));
        }

        const listScheduleItemForSaving = transformDataForServer(listItemForSaving);

        dispatch(setterGantItems({ wasChanged: false }));
        if (listScheduleItemForSaving.length) {
            commitCreate(
                getCommitBulkScheduleItem(listScheduleItemForSaving as ScheduleItemTypeInput[], connectionItemsId),
            );
        }
    };

    useEffectBuffer(
        () => {
            saveDataToServer(itemsList);
        },
        TIMEOUT_BEFORE_AUTO_SAVING,
        [itemsList],
        'useSaveToServer',
    );
};

export default useSaveToServer;
